import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { getPendingServiceWorkerUpdate } from '../serviceWorkerRefresh';

const ServiceWorkerContext = createContext({
  updateAvailable: false,
  updateStatus: 'idle',
  updateError: '',
  dismissUpdate: () => {},
  applyUpdate: async () => false,
});

// `vela:sw-update-available` is dispatched from src/index.js inside the
// onUpdate callback registered with serviceWorkerRegistration.register().
// Using a window event keeps this provider decoupled from the SW boot path
// (which runs before React mounts).
const SW_UPDATE_EVENT = 'vela:sw-update-available';

export function ServiceWorkerProvider({ children }) {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updateStatus, setUpdateStatus] = useState('idle');
  const [updateError, setUpdateError] = useState('');
  const pendingApplyUpdate = useRef(null);

  useEffect(() => {
    const handleUpdate = (event) => {
      pendingApplyUpdate.current = event.detail?.applyUpdate || null;
      setUpdateAvailable(Boolean(pendingApplyUpdate.current));
      setUpdateStatus(pendingApplyUpdate.current ? 'ready' : 'idle');
      setUpdateError('');
    };
    window.addEventListener(SW_UPDATE_EVENT, handleUpdate);
    // Registration can discover a waiting worker before React's mount effect.
    // Subscribe first, then read the persistent snapshot so no update is lost.
    const pending = getPendingServiceWorkerUpdate();
    if (pending) handleUpdate({ detail: pending });
    return () => window.removeEventListener(SW_UPDATE_EVENT, handleUpdate);
  }, []);

  const dismissUpdate = useCallback(() => {
    setUpdateAvailable(false);
    setUpdateStatus('idle');
    setUpdateError('');
  }, []);

  // Activation and reload are both gated behind this banner action. The
  // callback is scoped to this tab, so sibling chart tabs remain untouched.
  const applyUpdate = useCallback(async () => {
    if (!pendingApplyUpdate.current || updateStatus === 'applying') return false;

    setUpdateStatus('applying');
    setUpdateError('');
    try {
      await pendingApplyUpdate.current();
      return true;
    } catch (error) {
      setUpdateStatus('error');
      setUpdateError(error?.message || 'The update could not be applied. Please try again.');
      return false;
    }
  }, [updateStatus]);

  return (
    <ServiceWorkerContext.Provider
      value={{
        updateAvailable,
        updateStatus,
        updateError,
        dismissUpdate,
        applyUpdate,
      }}
    >
      {children}
    </ServiceWorkerContext.Provider>
  );
}

export function useServiceWorker() {
  return useContext(ServiceWorkerContext);
}
